import { useState } from 'react';

export default function CertificateGallery() {
  const [selectedCertificate, setSelectedCertificate] = useState<number | null>(null);

  const certificates = [
    {
      id: 1,
      title: 'Aviation Crew Medical Certification',
      program: 'Aviation Medical Training',
      image: 'https://public.readdy.ai/ai/img_res/df50918dd12e0c8b730cd696c8213637.jpg',
      description: 'Comprehensive certification for aviation crew members in emergency medical response and high-altitude crisis management.'
    },
    {
      id: 2,
      title: 'Maritime Medical Response Specialist',
      program: 'Maritime Crew Training',
      image: 'https://readdy.ai/api/search-image?query=prestigious-maritime-medical-certification-document-yacht-crew-offshore-emergency-response-elegant-navy-blue-accents-gold-embossed-seal-professional-certificate-design-cream-background-formal-maritime-credentials&width=800&height=600&seq=cert002maritime&orientation=landscape',
      description: 'Elite certification for maritime professionals in offshore medical emergency management and evacuation coordination.'
    },
    {
      id: 3,
      title: 'Estate Medical Response Professional',
      program: 'Estate Staff Training',
      image: 'https://readdy.ai/api/search-image?query=sophisticated-estate-medical-certification-document-household-staff-emergency-response-elegant-burgundy-gold-border-official-emblem-luxury-certificate-design-ivory-parchment-background-professional-credentials&width=800&height=600&seq=cert003estate&orientation=landscape',
      description: 'Professional certification for estate staff in discreet medical crisis management and residential emergency response.'
    },
    {
      id: 4,
      title: 'Tactical Medical Response Operator',
      program: 'Security Team Training',
      image: 'https://readdy.ai/api/search-image?query=authoritative-tactical-medical-certification-document-security-professionals-executive-protection-medical-response-black-gold-accents-official-seal-military-precision-design-cream-background-tactical-credentials&width=800&height=600&seq=cert004security&orientation=landscape',
      description: 'Advanced tactical certification integrating medical response with executive protection protocols.'
    },
    {
      id: 5,
      title: 'Luxury Hospitality Medical Readiness',
      program: 'Elite Hospitality Training',
      image: 'https://readdy.ai/api/search-image?query=refined-hospitality-medical-certification-document-luxury-hotel-staff-VIP-guest-emergency-response-elegant-teal-gold-design-sophisticated-seal-premium-certificate-layout-cream-parchment-hospitality-credentials&width=800&height=600&seq=cert005hospitality&orientation=landscape',
      description: 'Specialized certification for luxury hospitality professionals in VIP guest medical privacy and discreet emergency response.'
    },
    {
      id: 6,
      title: 'Custom Medical Training Certification',
      program: 'Bespoke Training Programs',
      image: 'https://readdy.ai/api/search-image?query=exclusive-bespoke-medical-certification-document-custom-training-program-elegant-platinum-gold-accents-unique-official-seal-tailored-certificate-design-premium-ivory-background-personalized-credentials&width=800&height=600&seq=cert006custom&orientation=landscape',
      description: 'Customized certification designed with surgical precision for specialized operational environments.'
    }
  ];

  return (
    <section className="bg-black py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#707070] font-light mb-4">
            Professional Credentials
          </div>
          <h2 className="text-[40px] sm:text-[48px] md:text-[56px] font-bold text-[#F5F5F5] mb-6 font-serif">
            Certificate Gallery
          </h2>
          <p className="text-[16px] text-[#9A9A9A] max-w-[800px] mx-auto leading-relaxed">
            Internationally recognized certifications that demonstrate your team's medical readiness and professional excellence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {certificates.map((certificate) => (
            <div
              key={certificate.id}
              onClick={() => setSelectedCertificate(certificate.id)}
              className="group relative bg-[#0D0D0D] border border-[#1A1A1A] rounded-2xl overflow-hidden hover:border-[#D4C4B0] transition-all duration-300 cursor-pointer"
            >
              <div className="relative h-[300px] overflow-hidden">
                <img
                  src={certificate.image}
                  alt={certificate.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300"></div>
                <div className="absolute top-4 right-4 w-12 h-12 bg-[#D4C4B0] rounded-full flex items-center justify-center">
                  <i className="ri-award-line text-[24px] text-black"></i>
                </div>
              </div>

              <div className="p-6">
                <div className="text-[11px] uppercase tracking-wider text-[#D4C4B0] mb-2">
                  {certificate.program}
                </div>
                <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-3 group-hover:text-[#D4C4B0] transition-colors duration-300">
                  {certificate.title}
                </h3>
                <p className="text-[14px] text-[#9A9A9A] leading-relaxed mb-4">
                  {certificate.description}
                </p>
                <div className="flex items-center gap-2 text-[13px] text-[#D4C4B0] font-semibold">
                  <span>View Certificate</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-[#0D0D0D] border border-[#1A1A1A] rounded-2xl p-8 md:p-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#D4C4B0] rounded-full flex items-center justify-center">
              <i className="ri-medal-line text-[40px] text-black"></i>
            </div>
          </div>
          <h3 className="text-[28px] font-bold text-[#F5F5F5] mb-4 font-serif">
            Earn Your Certification
          </h3>
          <p className="text-[16px] text-[#9A9A9A] max-w-[700px] mx-auto mb-8 leading-relaxed">
            All our training programs include internationally recognized certification upon successful completion. Your team's credentials demonstrate their commitment to medical excellence and professional readiness.
          </p>
          <a
            href="#contact"
            className="inline-block px-8 py-4 bg-[#D4C4B0] text-black text-[14px] font-semibold rounded-lg hover:bg-[#C4B4A0] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Start Your Certification Journey
          </a>
        </div>
      </div>

      {selectedCertificate && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCertificate(null)}
        >
          <div className="relative max-w-[1000px] w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedCertificate(null)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-close-line text-[24px] text-white"></i>
            </button>
            <img
              src={certificates.find(c => c.id === selectedCertificate)?.image}
              alt="Certificate Preview"
              className="w-full h-auto rounded-2xl"
            />
          </div>
        </div>
      )}
    </section>
  );
}
